import React, { useState } from "react";
import cn from "classnames";
import styles from "./PopularProducts.module.sass";
import Card from "../Card";
import Icon from "../Icon";
import Actions from "../Actions";


const Calendar = ({ className, views = 6, attendances = [], onMeetingClick }) => {
  // Obtenir la date actuelle
  const today = new Date();
  const [centerDate, setCenterDate] = useState(new Date(today));
  
  // Génération du mini-calendrier dynamique
  const generateCalendar = () => {
    const daysShort = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    
    // Générer 7 dates avec la date sélectionnée au centre (index 3)
    const dates = [];
    const dateObjects = [];
    
    for (let i = -3; i <= 3; i++) {
      const newDate = new Date(centerDate);
      newDate.setDate(centerDate.getDate() + i);
      dates.push(newDate.getDate());
      dateObjects.push(newDate);
    }
    
    return { days: daysShort, dates, dateObjects };
  };
  
  const { days, dates, dateObjects } = generateCalendar();
  
  // Convertir les attendances en format compatible avec le calendrier
  const convertAttendancesToMeetings = (attendances) => {
    return attendances.map(attendance => {
      const session = attendance.expand?.session_id;
      const formation = session?.expand?.formation_id;
      const teacher = session?.expand?.teacher_id;
      
      // Utiliser la date de début de session ou la date de création
      const sessionDate = new Date(session?.start_date || session?.created || attendance.created);
      
      return {
        id: attendance.id,
        title: formation?.title || session?.title || 'Session de formation',
        date: sessionDate,
        time: sessionDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        status: attendance.status || 'upcoming',
        type: 'formation',
        teacher: teacher ? `${teacher.first_name} ${teacher.last_name}` : null,
        formation: formation?.title,
        attendance: attendance
      };
    });
  };
  
  const meetings = convertAttendancesToMeetings(attendances);
  
  // Filtrer les réunions pour la date sélectionnée (centre)
  const selectedDateObj = dateObjects[3]; // L'objet Date au centre
  const selectedDay = selectedDateObj.getDate();
  const selectedMonth = selectedDateObj.getMonth();
  const selectedYear = selectedDateObj.getFullYear();
  
  // Filtrer les réunions pour la date sélectionnée (comparaison complète)
  const todayMeetings = meetings.filter(meeting => {
    return meeting.date.getDate() === selectedDay && 
           meeting.date.getMonth() === selectedMonth && 
           meeting.date.getFullYear() === selectedYear;
  }).sort((a, b) => {
    // Trier par heure croissante (plus tôt en premier)
    return a.date.getTime() - b.date.getTime();
  });
  
  // Fonction pour gérer le clic sur une date
  const handleDateClick = (clickedDate, index) => {
    // Utiliser l'objet Date correspondant
    const newCenterDate = new Date(dateObjects[index]);
    setCenterDate(newCenterDate);
  };
  
  // Fonction pour formater la date pour l'affichage
  const formatDateForDisplay = (dateObj) => {
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                   'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${dateObj.getDate()} ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
  };

  // Fonction pour revenir à aujourd'hui
  const goToToday = () => {
    const today = new Date();
    setCenterDate(new Date(today));
  };

  // Définition des actions pour le composant Actions
  const actions = [
    {
      title: "Rafraîchir",
      action: () => console.log("rafraîchir")
    },
    {
      title: "Aujourd'hui",
      action: goToToday
    }
  ];

  return (
    <>
      <Card
        className={cn(styles.card, className)}
        title="Calendrier"
        classTitle="title-blue"
        head={
           <Actions
            className={styles.actions}
            classActionsHead={styles.actionsHead}
            items={actions}
            small
          />
        }
      >
        <div className={styles.calendar}>
          {/* Mini Calendar */}
          <div className={styles.miniCalendar}>
            <div className={styles.calendarHeader}>
              {days.map((day, index) => (
                <div key={index} className={styles.dayName}>
                  {day}
                </div>
              ))}
            </div>
            <div className={styles.calendarDates}>
              {dates.map((date, index) => (
                <div 
                  key={index} 
                  className={cn(styles.date, {
                    [styles.selected]: index === 3, // Le centre est toujours sélectionné
                    [styles.today]: dateObjects[index].toDateString() === today.toDateString() // Marquer la date d'aujourd'hui
                  })}
                  onClick={() => handleDateClick(date, index)}
                >
                  {date}
                </div>
              ))}
            </div>
          </div>
          
          {/* Meetings List */}
          <div className={styles.meetingsList}>
            {todayMeetings.length > 0 ? (
              todayMeetings.map((meeting, index) => (
                <div
                  className={cn(styles.meetingItem, { [styles.clickable]: onMeetingClick })}
                  key={meeting.id || index}
                  onClick={() => onMeetingClick && onMeetingClick(meeting)}
                  style={{ cursor: onMeetingClick ? 'pointer' : 'default' }}
                >
                  <div className={styles.meetingIndicator}></div>
                  <div className={styles.meetingContent}>
                    <div className={styles.meetingTitle}>{meeting.title}</div>
                    <div className={styles.meetingDetails}>
                      <span className={styles.meetingStatus}>
                        {meeting.status === 'present' ? 'Présent' : 
                         meeting.status === 'absent' ? 'Absent' : 
                         meeting.status === 'late' ? 'Retard' : 'À venir'}
                      </span>
                      <span className={styles.meetingTime}>{meeting.time}</span>
                      {meeting.teacher && (
                        <span className={styles.meetingTeacher}>avec {meeting.teacher}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <p>Aucune session planifiée pour le {formatDateForDisplay(selectedDateObj)}.</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </>
  );
};

export default Calendar;
